/**
 * B614 (a′) — the SHOW face's calendar (554:2), pure half. What feeds what
 * (NOTE 555:249): every listed show's ON_SALE slots for the month, grouped by
 * the night they fall on; a day cell lists each show once with its times; a
 * tile opens that show's tickets step with the night chosen (its first
 * showtime carries the door); the arrows run from this month to the month of
 * the last show on sale. The words on a times line are the frame's: a
 * sold-out showtime says so after its time ("7:00 · 9:45 sold out"), a night
 * with a few-left showtime says so once ("7:00 · 9:45 · few left"), and an
 * unread availability says nothing — never a guessed state.
 *
 * The venue's own zone is the NOTE's ask; the storefront carries none today,
 * so the day is the buyer's local one, as on the poster wall (leg (a)).
 */
import type { PoolAvailability, SlotSummary } from "../ui/screens.js";
import { nightKey, showTime, showtimeState, type Zone } from "./posterWall.js";

export interface CalendarFeed {
  readonly productId: string;
  /** The headliner, as the poster names it. */
  readonly name: string;
  readonly slots: readonly SlotSummary[];
  readonly availability: Readonly<Record<string, readonly PoolAvailability[]>>;
}

export interface CalendarShow {
  readonly productId: string;
  readonly name: string;
  readonly times: string;
  readonly label: string;
  /** The night's first showtime — the tile's door preselects it. */
  readonly slotId: string;
  readonly start: number;
}

export interface CalendarDay {
  readonly day: number;
  readonly weekday: string;
  readonly today: boolean;
  readonly shows: readonly CalendarShow[];
}

const WEEKDAYS = ["SUN", "MON", "TUE", "WED", "THU", "FRI", "SAT"] as const;
const MONTHS = ["January", "February", "March", "April", "May", "June", "July", "August", "September", "October", "November", "December"] as const;

const split = (key: string): [number, number] => {
  const [y, m] = key.split("-").map(Number) as [number, number];
  return [y, m];
};
const keyOf = (y: number, m: number): string => `${String(y)}-${String(m).padStart(2, "0")}`;

/** "2026-10" — the month `now` falls in, on the calendar's clock. */
export function thisMonthKey(now: number, z: Zone): string {
  return nightKey(now, z).slice(0, 7);
}

/** "2026-10" ± n months. */
export function shiftMonth(key: string, delta: number): string {
  const [y, m] = split(key);
  const i = y * 12 + (m - 1) + delta;
  return keyOf(Math.floor(i / 12), (i % 12) + 1);
}

/** "September" — the arrow's word. */
export function monthName(key: string): string {
  return MONTHS[split(key)[1] - 1]!;
}

/** "October 2026" — the calendar's title. */
export function monthTitle(key: string): string {
  return `${monthName(key)} ${String(split(key)[0])}`;
}

const isLive = (s: SlotSummary, now: number): boolean => s.status === "ON_SALE" && s.start >= now;

/** The arrows' horizon: this month … the month of the last show on sale (never before this month). */
export function calendarSpan(feeds: readonly CalendarFeed[], now: number, z: Zone): { readonly first: string; readonly last: string } {
  const first = thisMonthKey(now, z);
  let last = first;
  for (const f of feeds) for (const s of f.slots) if (isLive(s, now)) {
    const m = nightKey(s.start, z).slice(0, 7);
    if (m > last) last = m;
  }
  return { first, last };
}

/** One night's times line, in the frame's words. */
export function calendarTimes(
  slots: readonly SlotSummary[],
  availability: Readonly<Record<string, readonly PoolAvailability[]>>,
  z: Zone,
): string {
  let few = false;
  const times = [...slots]
    .sort((a, b) => a.start - b.start)
    .map((s) => {
      const state = showtimeState(availability[s.slotId]);
      if (state === "few") few = true;
      return state === "soldout" ? `${showTime(s.start, z)} sold out` : showTime(s.start, z); // vocab-ok: the SHOW face's own chip words (554:2), drawn only for a SHOW tenant
    });
  return [...times, ...(few ? ["few left"] : [])].join(" · ");
}

/** The month's days, Sunday first: the blanks before the 1st and each day's shows in the order they start. */
export function calendarDays(
  feeds: readonly CalendarFeed[],
  month: string,
  now: number,
  z: Zone,
): { readonly leading: number; readonly days: readonly CalendarDay[] } {
  const [y, m] = split(month);
  const leading = new Date(Date.UTC(y, m - 1, 1)).getUTCDay();
  const length = new Date(Date.UTC(y, m, 0)).getUTCDate();
  const today = nightKey(now, z);
  const byNight = new Map<string, CalendarShow[]>();
  for (const f of feeds) {
    const nights = new Map<string, SlotSummary[]>();
    for (const s of f.slots) {
      if (!isLive(s, now)) continue;
      const key = nightKey(s.start, z);
      if (key.slice(0, 7) !== month) continue;
      nights.set(key, [...(nights.get(key) ?? []), s]);
    }
    for (const [key, slots] of nights) {
      const first = slots.reduce((a, b) => (b.start < a.start ? b : a));
      const times = calendarTimes(slots, f.availability, z);
      const day = Number(key.slice(8));
      const wd = WEEKDAYS[(leading + day - 1) % 7]!;
      const show: CalendarShow = {
        productId: f.productId,
        name: f.name,
        times,
        label: `${f.name}, ${wd.charAt(0)}${wd.slice(1).toLowerCase()} ${String(day)} — ${times}`,
        slotId: first.slotId,
        start: first.start,
      };
      byNight.set(key, [...(byNight.get(key) ?? []), show]);
    }
  }
  const days: CalendarDay[] = Array.from({ length }, (_, i) => {
    const day = i + 1;
    const key = `${month}-${String(day).padStart(2, "0")}`;
    return {
      day,
      weekday: WEEKDAYS[(leading + i) % 7]!,
      today: key === today,
      shows: [...(byNight.get(key) ?? [])].sort((a, b) => a.start - b.start),
    };
  });
  return { leading, days };
}
